import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import ShieldOutlinedIcon from "@material-ui/icons/ShieldOutlined";
import {
  PROTECTED_SPACE_GROUP_HINT,
  PROTECTED_SPACE_GROUP_LABEL,
  PROTECTED_SPACE_OPTIONS,
  STANDALONE_AMENITY_OPTIONS,
  hasAnyProtectedSpace
} from "utils/contacts/amenityLabels";

/**
 * The amenity checkboxes a realtor ticks on a CUSTOMER — every surface that edits
 * `GenericPropertyDetails` on a contact renders this, so ממ״ד / ממ״ק / מקלט are always adjacent
 * and always carry the "any of them will do" sentence.
 *
 * Why the group is drawn at all (2026-09-22): the three sat scattered among the other ten in a
 * two-column grid, and a realtor ticking all three of them meant "any shelter is fine" while every
 * matching surface read it as "all three, in the same building" — which no listing has. The server
 * now ORs them (`PROTECTED_SPACE_GROUP`); this block is what makes that legible BEFORE the realtor
 * wonders why the matches went empty.
 *
 * Ticking one does NOT tick the others (product decision, Haim): the realtor may genuinely mean
 * "ממ״ד only". The hint appears as soon as anything in the group is on, so the invitation to add
 * the alternatives is there at the moment it is relevant and silent otherwise.
 *
 * `onChange` hands back the WHOLE bag, every key present — an un-tick must reach the server as a
 * literal `false`, and a partial bag is how it would arrive as an omission instead.
 */
export function AmenityPicker({
  value,
  onChange,
  columns = 2,
  disabled
}: {
  value: Record<string, boolean | undefined>;
  onChange: (next: Record<string, boolean>) => void;
  columns?: number;
  disabled?: boolean;
}): JSX.Element {
  const toggle = (key: string): void => onChange({ ...(value as Record<string, boolean>), [key]: !value[key] });
  const groupActive = hasAnyProtectedSpace(value);

  const box = (key: string, label: string): JSX.Element => (
    <FormControlLabel
      key={key}
      disabled={disabled}
      sx={{ mx: 0, "& .MuiTypography-root": { typography: "body2" } }}
      control={<Checkbox size="small" checked={value[key] === true} onChange={() => toggle(key)} />}
      label={label}
    />
  );

  return (
    <Stack spacing={1.25}>
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          border: 1,
          borderColor: groupActive ? "primary.main" : "divider",
          bgcolor: (theme) => (groupActive ? alpha(theme.palette.primary.main, 0.06) : "transparent")
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "text.secondary", mb: 0.25 }}>
          <ShieldOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {PROTECTED_SPACE_GROUP_LABEL}
          </Typography>
        </Stack>
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5 }}>
          {PROTECTED_SPACE_OPTIONS.map((option) => box(option.key, option.label))}
        </Box>
        {/* Only once something is ticked: an always-on sentence here is noise on the majority of
            cards, which ask for no shelter at all. */}
        {groupActive && (
          <Typography variant="caption" sx={{ display: "block", color: "primary.dark" }}>
            {PROTECTED_SPACE_GROUP_HINT}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, columnGap: 1 }}>
        {STANDALONE_AMENITY_OPTIONS.map((option) => box(option.key, option.label))}
      </Box>
    </Stack>
  );
}
